import React, { useState, useEffect, useRef, useMemo } from "react";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import Highcharts from "highcharts";
import HighChartsReact from "highcharts-react-official";
import MatchStatsExpanded from "./MatchStatsExpanded.js";
import classnames from "classnames";
import ArrowButton from "./ArrowButton.js";
import { getKdaColorBestChampsClassName, getWinRateBestChampsColorClassName } from "lib/general-helper.js";
import { MediaQuery } from "@outplayed/responsive";

const MatchStats = (props) => {
  const { matchSummaryPages, summonerName } = props;

  const { getChampionImg, getChampionName } = getRiotAssetsContext();
  const [isOpen, setIsOpen] = useState(false);
  const chartRef = useRef();

  if (!matchSummaryPages) {
    return null;
  }

  const getTotalGames = () => {
    return matchSummaryPages.length * 20;
  };

  const getWinRate = (matches, championId, rounding) => {
    let wins = 0,
      losses = 0;
    if (championId) {
      for (const [key, value] of Object.entries(matches)) {
        for (const [subKey, gameData] of Object.entries(value)) {
          if (gameData["championId"] === parseInt(championId)) {
            gameData["win"] ? ++wins : ++losses;
          }
        }
      }
    } else {
      for (const [key, value] of Object.entries(matches)) {
        for (const [subKey, gameData] of Object.entries(value)) {
          gameData["win"] ? ++wins : ++losses;
        }
      }
    }
    return [((wins / (wins + losses)) * 100).toFixed(rounding), wins, losses];
  };

  const getOverallKDA = (matches) => {
    let kills = 0,
      deaths = 0,
      assists = 0,
      totalGames = 0;
    for (const [key, value] of Object.entries(matches)) {
      totalGames += value.length;
      for (const [subKey, gameData] of Object.entries(value)) {
        kills += gameData["kills"];
        assists += gameData["assists"];
        deaths += gameData["deaths"];
      }
    }
    return {
      kda: deaths === 0 ? "Perfect" : ((kills + assists) / deaths).toFixed(2),
      kills: (kills / totalGames).toFixed(1),
      assists: (assists / totalGames).toFixed(1),
      deaths: (deaths / totalGames).toFixed(1),
    };
  };

  const getTopThreeChampsPlayed = (matches) => {
    let allChampsPlayed = {};
    for (const [key, value] of Object.entries(matches)) {
      for (const [subKey, gameData] of Object.entries(value)) {
        if (!(gameData["championId"] in allChampsPlayed)) {
          allChampsPlayed[gameData["championId"]] = {
            games: 1,
            wins: gameData["win"] ? 1 : 0,
            losses: gameData["win"] ? 0 : 1,
            kills: gameData["kills"],
            assists: gameData["assists"],
            deaths: gameData["deaths"],
          };
        } else {
          allChampsPlayed[gameData["championId"]]["games"] += 1;
          allChampsPlayed[gameData["championId"]]["wins"] += gameData["win"] ? 1 : 0;
          allChampsPlayed[gameData["championId"]]["losses"] += gameData["win"] ? 0 : 1;
          allChampsPlayed[gameData["championId"]]["kills"] += gameData["kills"];
          allChampsPlayed[gameData["championId"]]["assists"] += gameData["assists"];
          allChampsPlayed[gameData["championId"]]["deaths"] += gameData["deaths"];
        }
      }
    }
    const allChampionPairs = Object.keys(allChampsPlayed).map((key) => [key, allChampsPlayed[key]]);
    const topThree = Object.fromEntries(allChampionPairs.sort((a, b) => b[1]["games"] - a[1]["games"]).splice(0, 3));
    return topThree;
  };

  const winrate = useMemo(() => {
    return getWinRate(matchSummaryPages)[0];
  }, [matchSummaryPages]);

  const wins = useMemo(() => {
    return getWinRate(matchSummaryPages)[1];
  }, [matchSummaryPages]);

  const losses = useMemo(() => {
    return getWinRate(matchSummaryPages)[2];
  }, [matchSummaryPages]);

  const kdaStats = useMemo(() => {
    return getOverallKDA(matchSummaryPages);
  }, [matchSummaryPages]);

  const topThreeChampions = useMemo(() => {
    return getTopThreeChampsPlayed(matchSummaryPages);
  }, [matchSummaryPages]);

  const totalGames = useMemo(() => {
    return getTotalGames(matchSummaryPages);
  }, [matchSummaryPages]);

  const options = {
    chart: {
      type: "pie",
      animation: false,
    },
    series: [
      {
        data: [
          {
            y: wins,
            color: "#3273FA",
          },
          {
            y: losses,
            color: "#FF4E50",
          },
        ],
        states: {
          hover: {
            enabled: false,
          },
        },
        enableMouseTracking: false,
      },
    ],
    plotOptions: {
      pie: {
        allowPointSelect: false,
        dataLabels: {
          enabled: false,
        },
        showInLegend: false,
        size: 28,
        innerSize: 16,
        borderWidth: 0,
      },
    },
    title: {
      text: "",
    },
    marker: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    xAxis: {
      labels: {
        enabled: false,
      },
    },
    yAxis: {
      labels: {
        enabled: false,
      },
    },
  };

  const chartCallback = (chart) => {
    if (chart) {
      chart.reflow();
    }
  };

  if (matchSummaryPages && matchSummaryPages[0] && matchSummaryPages[0].length === 0) {
    return null;
  } else if (topThreeChampions === {} || totalGames === 0 || kdaStats === {}) {
    return null;
  }

  return (
    <div>
      <div className={classnames("match-stats-container", { "expanded-container": !isOpen })}>
        <div className="flex-stats">
          <div className="pie-container">
            <HighChartsReact
              highcharts={Highcharts}
              ref={chartRef}
              options={options}
              containerProps={{
                style: { width: "100%", height: "100%" },
              }}
              callback={chartCallback}
            />
          </div>
          <div className="stats-winrate">
            <div className="bold-header">{getWinRate(matchSummaryPages)[0]}% WR</div>
            <span className="sub-desc">Last {totalGames} games</span>
          </div>
          <div className="overall-kda-stats">
            <div className="bold-header">{kdaStats["kda"]} KDA</div>
            <div className="sub-desc">
              {kdaStats["kills"]}
              <span className="slash"> / </span>
              {kdaStats["deaths"]}
              <span className="slash"> / </span>
              {kdaStats["assists"]}
            </div>
          </div>
        </div>
        <div className="top-three-champions">
          {Object.keys(topThreeChampions).map((key) => (
            <div className="most-played-champ-container" key={key}>
              <div className="image-container">
                <img src={getChampionImg(key)} alt={getChampionName(key)} className="champ-image" />
              </div>
              <div className="stats">
                <div className="text">
                  <span
                    className={classnames(
                      "winrate",
                      getWinRateBestChampsColorClassName(getWinRate(matchSummaryPages, key, 0)[0]),
                    )}
                  >
                    {getWinRate(matchSummaryPages, key, 0)[0]}%{" "}
                  </span>
                  <span className="wins-losses">
                    ({topThreeChampions[key]["wins"]}W {topThreeChampions[key]["losses"]}L)
                  </span>
                </div>
                <div
                  className={classnames(
                    "kda",
                    getKdaColorBestChampsClassName(
                      topThreeChampions[key]["deaths"] === 0
                        ? "Perfect"
                        : (
                            (topThreeChampions[key]["kills"] + topThreeChampions[key]["assists"]) /
                            topThreeChampions[key]["deaths"]
                          ).toFixed(2),
                    ),
                  )}
                >
                  {topThreeChampions[key]["deaths"] === 0
                    ? "Perfect"
                    : (
                        (topThreeChampions[key]["kills"] + topThreeChampions[key]["assists"]) /
                        topThreeChampions[key]["deaths"]
                      ).toFixed(2)}{" "}
                  KDA
                </div>
              </div>
            </div>
          ))}
        </div>
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
          <div>
            <ArrowButton isOpen={isOpen} toggleFunction={setIsOpen} />
          </div>
        </MediaQuery>
      </div>
      {isOpen && <MatchStatsExpanded matchSummaryPages={matchSummaryPages} summonerName={summonerName} totalGames={totalGames} />}
    </div>
  );
};

export default MatchStats;
